import {
  Box,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField
} from '@mui/material';
import React from 'react';
import { auth, retrieveMultiFactorResolver } from '../../../lib/google/firebase';
import { FirebaseError } from '@firebase/util';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MetadataError } from '@ep/error-handling';
import { log } from '../../../util/log';
import { generateUuidV4 } from '../../../util/uuid';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  MultiFactorResolver,
  TotpMultiFactorAssertion,
  TotpMultiFactorGenerator
} from 'firebase/auth';
import GhostButton from '../../shared/button/GhostButton';
import { LoginState } from './LoginModal';
import { MFA_TIMEOUT_MS } from '../../../env';
import { trackEvent } from '../../../lib/app/analytics/ga';

const default_mfa_error = 'Unknown Error Occured';

/**
 * Handles the verification of an enrolled client authenticator.
 * @param {(authenticated: boolean): void} setAuthenticated Sets the global authenticated state of the application
 * @param {(): void} closeLogin Closes the sign in process completely and resets it to initial sign in step.
 * @returns {JSX.Element}
 */
const VerifyAuthenticator = ({
  handleLoginState,
  setAuthenticated,
  closeLogin
}: {
  handleLoginState: (login_state: LoginState) => void;
  setAuthenticated: (authenticated: boolean) => void;
  closeLogin: () => void;
}): JSX.Element => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [code, setCode] = React.useState<string>('');
  const [validCode, setValidCode] = React.useState<boolean>(false);
  const [verifyLoad, setVerifyLoad] = React.useState<boolean>(false);
  const [loginError, setLoginError] = React.useState<boolean>(false);
  const [loginErrorMessage, setLoginErrorMessage] = React.useState<string>(default_mfa_error);
  const navigate: NavigateFunction = useNavigate();

  // Handles return to sign in modal if user cancels authenticator sign in.
  const handleBack = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    handleLoginState('login_identifier');
  };

  // Handles change of authenticator code state.
  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setCode(event.target.value);
  };

  // Handles the changing of error text for failed MFA verification.
  const handleMfaError = (state: boolean, message: string): void => {
    setLoginError(state);
    setLoginErrorMessage(message);
  };

  // Handles the closing of the login modal and resetting state to sing in step.
  const handleClose = (): void => {
    closeLogin();
    setVerifyLoad(false);
  };

  // Handles of closing the MFA verification before it times out to force user to restart process
  React.useEffect(() => {
    const timeout_id: NodeJS.Timeout = setTimeout(async () => {
      handleClose();
    }, MFA_TIMEOUT_MS);
    return () => clearTimeout(timeout_id);
  }, []);

  // Handles the verification of the authenticator code provided by the user.
  const handleMfaVerification = async (
    event: React.MouseEvent<HTMLButtonElement>
  ): Promise<void> => {
    event.preventDefault();
    try {
      setVerifyLoad(true);
      const mfa_resolver: MultiFactorResolver | null = retrieveMultiFactorResolver();
      if (mfa_resolver) {
        const multiFactorAssertion: TotpMultiFactorAssertion =
          TotpMultiFactorGenerator.assertionForSignIn(mfa_resolver.hints[0].uid, code);
        await mfa_resolver.resolveSignIn(multiFactorAssertion);
      }
      // Send user login event to Google Analytics.
      trackEvent(
        'user_login',
        {
          syntiro_user_id: auth.currentUser?.uid,
          syntiro_user_email: auth.currentUser?.email
        },
        false
      );
      setCode('');
      setAuthenticated(true);
      navigate('/');
      handleClose();
      if (loginError === true) handleMfaError(false, default_mfa_error);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      if ((err as FirebaseError).code == 'auth/invalid-verification-code') {
        handleMfaError(true, 'Incorrect verification code, please try again!');
      } else if ((err as FirebaseError).code == 'auth/too-many-requests') {
        handleMfaError(true, 'Too many login requests, please try again later');
      } else if ((err as FirebaseError).code == 'auth/quota-exceeded') {
        handleMfaError(true, 'Too many verification requests, please try again later');
      } else {
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: VerifyAuthenticator failed on an unknown error while calling handleMfaVerification.',
            err instanceof FirebaseError
              ? {
                  firebase_error_code: err.code,
                  code: code
                }
              : {
                  code: code
                },
            tracking_id
          )
        );
        setFeedbackData({
          message: `An unknown error has occured. Tracking ID: ${tracking_id}`,
          state: true,
          type: 'error'
        });
      }
      setVerifyLoad(false);
    }
  };

  // Handles the debounce of the user code input and handles the validity of its input.
  React.useEffect(() => {
    setTimeout(() => {
      setValidCode(code.length > 0);
    }, 250);
    return () => {
      null;
    };
  }, [code]);

  return (
    <Box>
      <DialogTitle>Authenticator Verification</DialogTitle>
      <DialogContentText sx={{ px: 3, py: 1 }}>
        Please input your code from your authenticator application
      </DialogContentText>
      <form>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="text"
            label="Verification Code"
            type="text"
            fullWidth
            variant="outlined"
            onChange={handleCodeChange}
            sx={{ my: 2 }}
          />
          {loginError && (
            <DialogContentText sx={{ color: 'red' }}>{loginErrorMessage}</DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <PrimaryButton
            loading={verifyLoad}
            data-testid="verify-button"
            onClick={handleMfaVerification}
            sx={{ minWidth: 150 }}
            disabled={!validCode}
            type="submit"
          >
            Verify
          </PrimaryButton>
          <GhostButton
            id="send-code-button"
            outlined
            dark
            sx={{ minWidth: 150 }}
            onClick={handleBack}
          >
            Back
          </GhostButton>
        </DialogActions>
      </form>
    </Box>
  );
};

export default VerifyAuthenticator;
